import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [polling, setPolling] = useState(true);
  const [attempts, setAttempts] = useState(0);
  const { clear } = useCart();

  useEffect(() => {
    if (!session_id) return;
    let mounted = true;
    let n = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${session_id}`);
        if (!mounted) return;
        setStatus(data);
        setAttempts(n + 1);
        if (data.payment_status === "paid") {
          setPolling(false);
          clear();
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setPolling(false);
          return;
        }
        n++;
        if (n >= 15) { setPolling(false); return; }
        setTimeout(poll, 2000);
      } catch {
        setPolling(false);
      }
    };
    poll();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session_id]);

  const paid = status.payment_status === "paid";

  return (
    <section className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="payment-success">
      <div className="rounded-3xl bg-white p-12 ring-1 ring-silver-light shadow-lg">
        {polling && !paid && (
          <>
            <Clock size={56} className="mx-auto text-navy-royal animate-pulse" />
            <h1 className="font-serif text-3xl text-navy-dark mt-4">Confirming your payment…</h1>
            <p className="text-navy/70 mt-3 text-sm">Attempt {attempts}/15 — this usually takes a few seconds.</p>
          </>
        )}
        {paid && (
          <>
            <CheckCircle2 size={64} className="mx-auto text-navy-royal" />
            <h1 className="font-serif text-4xl text-navy-dark mt-4">Order confirmed.</h1>
            <p className="text-navy/70 mt-3">Thank you for supporting Collins Crafty Creations! A receipt has been emailed to you.</p>
            {status.amount && <div className="mt-6 text-2xl text-navy-royal font-semibold" data-testid="paid-amount">${Number(status.amount).toFixed(2)} USD</div>}
          </>
        )}
        {!polling && !paid && (
          <>
            <XCircle size={56} className="mx-auto text-cpr" />
            <h1 className="font-serif text-3xl text-navy-dark mt-4">Payment not confirmed</h1>
            <p className="text-navy/70 mt-3 text-sm">If you completed checkout, please refresh or contact us.</p>
          </>
        )}
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/shop" className="rounded-full bg-navy-dark text-white px-6 py-3 text-sm font-medium" data-testid="ps-shop">Continue shopping</Link>
          <Link to="/" className="rounded-full border border-silver-light px-6 py-3 text-sm font-medium">Home</Link>
        </div>
      </div>
    </section>
  );
}

export function PaymentCancel() {
  return (
    <section className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="payment-cancel">
      <div className="rounded-3xl bg-white p-12 ring-1 ring-silver-light shadow-lg">
        <XCircle size={56} className="mx-auto text-cpr" />
        <h1 className="font-serif text-3xl text-navy-dark mt-4">Payment cancelled</h1>
        <p className="text-navy/70 mt-3">No charge was made. Your cart is still saved.</p>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/shop" className="rounded-md bg-navy-dark text-white px-6 py-3 text-sm font-semibold hover:bg-navy-royal transition-colors" data-testid="pc-shop">Back to shop</Link>
        </div>
      </div>
    </section>
  );
}
